import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useWalkthrough } from '../useWalkthrough';
import { CREATE_MODEL_WALKTHROUGH_ID } from '../registry/createModelWalkthrough';
import { DemoTourScopeContext } from '../demo/demoTourScope';
import { deferUntilFocusWithin, shouldDeferUntilFocus } from '../utils/focusDeferral';

/** True while the walkthrough's coach mark for `stepId` is the one on screen. */
export function useWalkthroughStepActive(stepId) {
    const { visibleStepId } = useWalkthrough();
    return Boolean(stepId) && visibleStepId === stepId;
}

/**
 * True inside the read-only "Show me how" guided demo: while its walkthrough
 * is running, and still on the demo wizard's Train page after the last mark
 * (the tour ends on Data Editor; `demo/DemoScope.jsx` provides
 * `DemoTourScopeContext` for as long as the demo wizard is mounted). Hosts
 * use it for the demo's presets (System prompt on, Advanced open, locked
 * dropdowns, …) that must not leak into the live wizard — with or without
 * the Spark assistant, which also provides a `WizardApiProvider`.
 */
export function useDemoTourActive() {
    const { active } = useWalkthrough();
    const demoWizard = useContext(DemoTourScopeContext);
    return demoWizard || active === CREATE_MODEL_WALKTHROUGH_ID;
}

/**
 * Let the walkthrough drive a boolean the host page owns (a section switch,
 * an "Advanced" toggle): it is turned on when the step becomes active and
 * restored to what it was when the step stops being active — the user
 * skipped it, dismissed the tour, moved to another step or page — unless
 * `keepIf` says the user made the state their own (typed into the field, …).
 *
 * `setValue` is called with a plain boolean, so pass the state setter itself
 * or a wrapper that accepts one.
 *
 * `deferWhileTyping` (a selector for the driven control's anchor) keeps the
 * user's caret where it is: when the step activates while a text-entry
 * element outside that anchor owns focus, the value is only turned on once
 * focus reaches the anchor (Tab or click). Nothing is restored on leave if
 * focus never arrived.
 */
export function useWalkthroughDrivenState(
    stepId,
    value,
    setValue,
    { keepIf = false, deferWhileTyping = null } = {},
) {
    const active = useWalkthroughStepActive(stepId);
    const previousRef = useRef(null);
    const latest = useRef({ value, setValue, keepIf });
    latest.current = { value, setValue, keepIf };

    useEffect(() => {
        if (!active) return undefined;
        const drive = () => {
            previousRef.current = latest.current.value;
            latest.current.setValue(true);
        };
        const stopDeferring = shouldDeferUntilFocus(deferWhileTyping)
            ? deferUntilFocusWithin(deferWhileTyping, drive)
            : null;
        if (!stopDeferring) drive();
        return () => {
            stopDeferring?.();
            const previous = previousRef.current;
            previousRef.current = null;
            const keep = typeof latest.current.keepIf === 'function' ? latest.current.keepIf() : latest.current.keepIf;
            if (!keep && previous !== null) latest.current.setValue(previous);
        };
    }, [active, deferWhileTyping]);
}

/**
 * `useState(false)` for a menu's `open`, except the walkthrough opens it when
 * `stepId` becomes active (and closes it again when the step is left while it
 * is still open). Returns `[open, setOpen, locked]`.
 *
 * Pass `{ deferWhileTyping: '<anchor selector>' }` for a menu whose open
 * panel grabs focus (Spark searchable selects): it then opens only once
 * focus reaches the anchor if the user was typing elsewhere.
 *
 * Pass `{ lockOpen: true }` (the demo tour) to keep the menu open while the
 * step is active: the host's attempts to close it (outside click, Escape,
 * the trigger) are ignored, so the user can only move on by picking the
 * hinted option. `locked` tells the host it is in that state. Picking an
 * option is the one close that must go through even then — call
 * `setOpen(false, { force: true })` from the option's handler.
 */
export function useWalkthroughAutoOpen(stepId, { lockOpen = false, ...options } = {}) {
    const [open, setOpenState] = useState(false);
    const active = useWalkthroughStepActive(stepId);
    const locked = lockOpen && active;
    const lockedRef = useRef(locked);
    lockedRef.current = locked;
    const setOpen = useCallback((next, { force = false } = {}) => {
        setOpenState((previous) => {
            const value = Boolean(typeof next === 'function' ? next(previous) : next);
            return lockedRef.current && !force && !value && previous ? previous : value;
        });
    }, []);
    // The walkthrough's own drive bypasses the lock (it opens, and closes on leave once unlocked).
    const setOpenBoolean = useCallback((next) => setOpenState(Boolean(next)), []);
    useWalkthroughDrivenState(stepId, open, setOpenBoolean, options);
    return [open, setOpen, locked];
}
